import { createContext, useContext, useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { User, RegisterData, LoginResponse } from "../types/auth";
import { authApi } from "../api/auth";
import { userApi } from "../api/user";
import { notificationApi } from "../api/notification";
import { Skeleton } from "@/components/ui/skeleton";
import { normalizeEmail } from "@/utils/emailUtils";
import { isNative } from "@/utils/platform";

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  register: (userData: RegisterData) => Promise<void>;
  updateUserField: (userId: number, field: string, value: any) => Promise<void>;
  deactivateAccount: (userId: number, password: string) => Promise<void>;
  deleteAccount: (userId: number, password: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  const publicRoutes = [
    '/',
    '/login',
    '/register',
    '/forgot-password',
    '/reset-password',
    '/verify-email',
    '/about',
    '/contact',
    '/privacy-policy',
    '/terms-of-service',
    '/faq',
    '/impressum',
    '/create-wishlist',
    '/randomizer-info',
    '/smart-wishlists',
    '/ai-gift-assistant',
    '/social-gifting',
    '/give-whats-wanted',
    '/price-drop-alerts',
    '/event-management',
    '/chrome-extension'
  ];
  const isPublicRoute = publicRoutes.includes(location.pathname);

  useEffect(() => {
    const initializeAuth = async () => {
      const isPublicRoute = publicRoutes.includes(location.pathname);

      try {
        await authApi.bootstrapNativeAuthCache();
      } catch (e) {
        console.warn('Native auth bootstrap failed', e);
      }

      const REFRESH_LOCK_KEY = 'buyfors_refresh_lock';
      const REFRESH_RESULT_KEY = 'buyfors_refresh_result';
      
      const lock = localStorage.getItem(REFRESH_LOCK_KEY);
      if (lock) {
        const lockTime = parseInt(lock);
        if (isNaN(lockTime) || Date.now() - lockTime > 10000) {
          localStorage.removeItem(REFRESH_LOCK_KEY);
          localStorage.removeItem(REFRESH_RESULT_KEY);
        }
      }
      
      const result = localStorage.getItem(REFRESH_RESULT_KEY);
      if (result) {
        try {
          const parsed = JSON.parse(result);
          if (!parsed.timestamp || Date.now() - parsed.timestamp > 10000) {
            localStorage.removeItem(REFRESH_RESULT_KEY);
          }
        } catch (e) {
          localStorage.removeItem(REFRESH_RESULT_KEY);
        }
      }

      await new Promise(resolve => setTimeout(resolve, 50));

      const isSSO = location.search.includes('code=') || location.search.includes('state=') || sessionStorage.getItem('ssoInProgress') === 'true';

      const ssoProcessed = sessionStorage.getItem('ssoProcessed');

      try {
        setIsLoading(true);

        if (isPublicRoute && !authApi.hasStoredUserId() && (!isSSO || ssoProcessed)) {
          setIsLoading(false);
          return;
        }

        if (isPublicRoute && location.pathname === '/login' && authApi.hasStoredUserId() && authApi.hasAuthTokens()) {
          const userData = await authApi.validate(true);
          if (userData) {
            setUser(userData as User);
            const { tokenManager } = await import('../api/auth/tokenManager');
            tokenManager.resetRefreshFailed();
          }
          setIsLoading(false);
          return;
        }

        if (isPublicRoute && location.pathname === '/register' && authApi.hasStoredUserId() && authApi.hasAuthTokens()) {
          const userData = await authApi.validate(true);
          if (userData) {
            setUser(userData as User);
            const { tokenManager } = await import('../api/auth/tokenManager');
            tokenManager.resetRefreshFailed();
          }
          setIsLoading(false);
          return;
        }

        if (authApi.hasStoredUserId() || (isSSO && !ssoProcessed)) {
          if (isSSO && !ssoProcessed) {
            const userData = await authApi.validate(true);
            if (userData) {
              setUser(userData as User);
              if (userData.id) {
                authApi.setUserId(userData.id);
              }
              const { tokenManager } = await import('../api/auth/tokenManager');
              tokenManager.resetRefreshFailed();
              
              sessionStorage.setItem('ssoProcessed', 'true');
              sessionStorage.removeItem('ssoInProgress');

              const cleanUrl = window.location.pathname;
              window.history.replaceState({}, document.title, cleanUrl);

              await new Promise(resolve => setTimeout(resolve, 100));

              const storedReturnUrl = sessionStorage.getItem('loginReturnUrl');
              if (storedReturnUrl) {
                sessionStorage.removeItem('loginReturnUrl');
              }
              const returnUrl = storedReturnUrl || location.state?.from || '/';
              navigate(returnUrl, { replace: true });
            } else {
              if (!isPublicRoute) {
                authApi.clearUserId();
                setUser(null);
              }
              sessionStorage.removeItem('ssoProcessed');
              sessionStorage.removeItem('ssoInProgress');
            }
          } else {
            const userData = await authApi.validate(true);
            if (userData) {
              setUser(userData as User);
              const { tokenManager } = await import('../api/auth/tokenManager');
              tokenManager.resetRefreshFailed();
            } else {
              if (!isPublicRoute) {
                authApi.clearUserId();
                setUser(null);
              }
            }
          }
        } else {
          if (!isPublicRoute) {
            setUser(null);
          }
        }
      } catch (error: any) {
        const errorMessage = error?.message || '';

        // Differentiate between network errors and auth errors
        if (errorMessage.includes('NETWORK_TIMEOUT') || errorMessage.includes('fetch')) {
          // Network error - show warning but don't logout immediately
          toast.warning('Connection issues detected. You may need to refresh if this persists.');
          
          // Keep the user logged in with stored data
          if (authApi.hasStoredUserId()) {
            // Don't clear user, give grace period
            return;
          }
        } else if (errorMessage.includes('SESSION_EXPIRED') ||
                   errorMessage.includes('NO_VALID_SESSION') ||
                   errorMessage.includes('REFRESH_TOKEN_EXPIRED')) {
          authApi.clearUserId();
          setUser(null);
          if (!isPublicRoute) {
            toast.error('Your session has expired. Please login again.');
            navigate('/login');
          } else if (isNative()) {
            navigate('/login', { replace: true });
          }
        } else if (isSSO) {
          if (!isPublicRoute) {
            authApi.clearUserId();
            setUser(null);
            
            sessionStorage.removeItem('ssoProcessed');
            sessionStorage.removeItem('ssoInProgress');

            const cleanUrl = window.location.pathname;
            window.history.replaceState({}, document.title, cleanUrl);

            toast.error('Login failed. Please try again.');
            navigate('/login');
          }
        } else {
          // Unknown error - show warning but keep user logged in if possible
          toast.warning('Unable to verify session. Please refresh if you experience issues.');
        }
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, [location.pathname, navigate]);

  useEffect(() => {
    if (!user?.id) return;

    notificationApi.connectWebSocket(user.id);
    const unsubscribe = notificationApi.onNotification(() => {
      try {
        queryClient.invalidateQueries({ queryKey: ['notifications'] });
      } catch (e) {
        // ignore cache invalidation failures
      }
    });

    let appStateListener: { remove: () => void } | null = null;
    if (isNative()) {
      import('@/services/pushNotifications')
        .then(({ initPushNotifications, clearDeliveredPushNotifications }) => {
          void initPushNotifications(user.id, (path: string) => navigate(path));
          void clearDeliveredPushNotifications();
        })
        .catch((e) => console.warn('Failed to load pushNotifications service', e));

      import('@capacitor/app')
        .then(({ App }) => {
          App.addListener('appStateChange', async ({ isActive }) => {
            if (!isActive) return;
            // Adopt any token the share extension refreshed while we were
            // backgrounded before anything else runs — a stale refresh token
            // surviving here risks the backend flagging it as token reuse.
            try {
              const { nativeTokenStore } = await import('@/api/auth/nativeTokenStore');
              await nativeTokenStore.syncFromShared();
              await nativeTokenStore.syncToShared();
            } catch (e) {
              console.warn('Failed to re-sync shared tokens on foreground', e);
            }
            try {
              const { clearDeliveredPushNotifications, ensurePushTokenRegistered } = await import('@/services/pushNotifications');
              await clearDeliveredPushNotifications();
              if (user?.id) {
                void ensurePushTokenRegistered(user.id);
              }
              queryClient.invalidateQueries({ queryKey: ['notifications'] });
            } catch (e) {
              console.warn('Failed to handle app foreground', e);
            }
          }).then((handle) => {
            appStateListener = handle;
          }).catch(() => { /* ignore */ });
        })
        .catch(() => { /* ignore */ });
    }

    return () => {
      unsubscribe();
      notificationApi.disconnectWebSocket();
      // Push registration is intentionally NOT torn down here. It belongs to the
      // logged-in session, not to this effect. Logout handles the unregister.
      if (appStateListener) {
        try { appStateListener.remove(); } catch { /* ignore */ }
      }
    };
  }, [user?.id, queryClient, navigate]);

  const login = async (email: string, password: string) => {
    try {
      const normalizedEmail = normalizeEmail(email);
      const loginResponse: LoginResponse = await authApi.login({ email: normalizedEmail, password });

      authApi.setUserId(loginResponse.user_id);

      const userData = await authApi.validate(true, loginResponse.user_id);
      setUser(userData as User);

    } catch (error) {
      throw error;
    }
  };

  const logout = async () => {
    try {
      setIsLoading(true);

      if (isNative() && user?.id) {
        try {
          const { unregisterPushNotifications } = await import('@/services/pushNotifications');
          await unregisterPushNotifications(user.id);
        } catch (e) {
          console.warn('Failed to unregister push on logout', e);
        }
      }

      try {
        notificationApi.disconnectWebSocket();
      } catch { /* ignore */ }

      const { tokenManager } = await import('../api/auth/tokenManager');
      tokenManager.setLoggingOut(true);

      await authApi.logout();
      authApi.clearUserId();
      setUser(null);

    } catch (error) {
      authApi.clearUserId();
      setUser(null);
      navigate('/', { replace: true });
      throw error;
    } finally {
      const { tokenManager } = await import('../api/auth/tokenManager');
      tokenManager.setLoggingOut(false);
      setIsLoading(false);
    }
  };

  const register = async (userData: RegisterData) => {
    try {
      const normalizedUserData = {
        ...userData,
        email: normalizeEmail(userData.email),
      };
      await authApi.register(normalizedUserData);
      navigate('/login');
    } catch (error) {
      throw error;
    }
  };

  const updateUserField = async (userId: number, field: string, value: any) => {
    try {
      if (field === 'email') {
        value = normalizeEmail(value);
      }
      const updatedUser = await userApi.updateField(userId, field, value);
      setUser(updatedUser as User);
    } catch (error) {
      throw error;
    }
  };

  /**
   * Tears down every piece of client-side session state after the server has
   * already invalidated the account (delete or deactivate). We can NOT call
   * authApi.logout() here — the server-side /user/logout would 401 because
   * the user/session is already gone, leaving stale cookies, stored user_id,
   * tokens, WebSocket and TanStack Query cache behind. The next login with a
   * different account then races against `initializeAuth` validating the dead
   * user_id and fails.
   *
   * We mirror what authApi.logout() does on the client side (push unregister,
   * WS disconnect, native token wipe, cookie/session clears, query cache
   * reset) and then hard-reload to /login so no module-level state survives.
   */
  const cleanupAfterAccountTermination = async () => {
    try {
      if (isNative() && user?.id) {
        try {
          const { unregisterPushNotifications } = await import('@/services/pushNotifications');
          await unregisterPushNotifications(user.id);
        } catch (e) {
          console.warn('Failed to unregister push after account termination', e);
        }
      }

      try { notificationApi.disconnectWebSocket(); } catch { /* ignore */ }

      const { tokenManager } = await import('../api/auth/tokenManager');
      tokenManager.setLoggingOut(true);

      try { queryClient.clear(); } catch { /* ignore */ }

      await authApi.clearAllClientAuthState();
    } finally {
      setUser(null);
      // Hard navigation: nukes React tree, in-memory tokenManager, any
      // remaining cache or singleton state from the terminated session.
      window.location.replace('/login');
    }
  };

  const deactivateAccount = async (userId: number, password: string) => {
    await userApi.deactivate(userId, { password });
    await cleanupAfterAccountTermination();
  };

  const deleteAccount = async (userId: number, password: string) => {
    await userApi.delete(userId, { password });
    await cleanupAfterAccountTermination();
  };

  if (isLoading && !user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background to-secondary/20 flex items-center justify-center">
        <div className="w-full max-w-md space-y-4 p-6">
          <Skeleton className="h-12 w-3/4 mb-4" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-2/3" />
          <Skeleton className="h-10 w-full mt-6" />
        </div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{
      user,
      isLoading,
      login,
      logout,
      register,
      updateUserField,
      deactivateAccount,
      deleteAccount
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
